import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { array, bool, string } from 'prop-types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiImg, Modal, Slide, Slider } from 'components';
import { useSafeState } from 'hooks';

const MainImage = ({ isTicketMaster, mainImgSrc }) => {
  return (
    <>
      <div className={clsx('h-full shrink-0 w-full', isTicketMaster && 'blur-[7px]')}>
        <ApiImg
          src={mainImgSrc}
          width="200"
          height="200"
          className="w-full h-full object-cover object-top lg:object-center"
        />
      </div>

      {isTicketMaster && (
        <div className="rounded-md overflow-hidden absolute z-0 translate-center-center w-[150px] h-[195px]">
          <ApiImg src={mainImgSrc} width="150" height="195" />
        </div>
      )}
    </>
  );
};

const Gallery = ({ images, loading, className = '', singleImage = false, isTicketMaster = false }) => {
  const { t } = useTranslation();
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useSafeState(false);

  const multipleGallery = !singleImage && images?.length > 1;

  const mainImgSrc = useMemo(() => {
    const mainImg = images?.find?.((image) => image.main);
    const visualImg = images?.find?.((image) => image.visual)?.visual;

    if (mainImg) return mainImg.url;
    else if (visualImg) return visualImg;
    else return images?.[0]?.url;
  }, [images]);

  return (
    <>
      {!loading && !isEmpty(images) && (
        <div className={clsx(className)}>
          {/* Mobile view */}
          <div className="relative h-72 mt-16 md:h-96 lg:hidden print:hidden">
            <MainImage mainImgSrc={mainImgSrc} isTicketMaster={isTicketMaster} />

            {multipleGallery && (
              <button type="button" className="btn-mobile" onClick={() => setIsGalleryModalOpen(true)}>
                {t('gallery', { count: images.length })}
              </button>
            )}
          </div>

          {/* Desktop view */}
          <div className="gallery">
            <div className="relative w-full">
              <MainImage mainImgSrc={mainImgSrc} isTicketMaster={isTicketMaster} />
            </div>

            {multipleGallery && (
              <div className="grid grid-cols-2 auto-rows-fr gap-2 overflow-hidden relative w-1/2">
                {images.slice(1, 5).map((image, index) => (
                  <ApiImg
                    key={`image-${index}`}
                    src={image.url}
                    width="200"
                    height="200"
                    className={clsx(images.length % 2 === 0 && index === images.length - 2 && 'col-span-2')}
                  />
                ))}

                <button type="button" className="btn-gallery" onClick={() => setIsGalleryModalOpen(true)}>
                  {t('gallery', { count: images.length })}
                </button>
              </div>
            )}
          </div>

          <Modal
            size="large"
            isOpen={isGalleryModalOpen}
            body={
              <Slider type="loop" options={{ perPage: 1, perMove: 1, gap: '1rem' }} sliderClass="arrows-gallery">
                {images.map((image, i) => (
                  <Slide key={`gallery-slider-${i}`} slideClass="flex justify-center">
                    <div className="h-64 w-full rounded-2xl overflow-hidden md:h-fit md:max-h-[38rem]">
                      <ApiImg src={image.url} alt={image.alt ?? ''} width="1280" height="650" />
                    </div>
                  </Slide>
                ))}
              </Slider>
            }
            onClose={() => setIsGalleryModalOpen(false)}
          />
        </div>
      )}
    </>
  );
};

Gallery.propTypes = {
  images: array,
  loading: bool,
  className: string,
  singleImage: bool,
  isTicketMaster: bool
};

MainImage.propTypes = {
  isTicketMaster: bool,
  mainImgSrc: string
};

export default Gallery;
